import { useState } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import api from "../lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { X, Save, AlertTriangle } from "lucide-react";

const branchCodeOf = (b) => (b.code || (b.pos_branch_code || "").split("-")[0] || "").toUpperCase();

const EMPTY_FORM = {
  branch_id: "", display_label: "", vendor_name: "", vendor_phone: "", vendor_email: "",
  orders_email: "", bank_name: "", bank_account_number: "", bank_clabe: "", account_holder: "", notes: "",
};

/** Modal reutilizable para alta/edición de una operación por sucursal de un
 * proveedor. Usado tanto desde SupplierDetail (ficha) como desde
 * SupplierBranchOpsTab (editor completo del proveedor). */
export default function BranchOperationModal({ supplier, branches, editingOp, presetBranchId, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(
    editingOp
      ? {
          branch_id: editingOp.branch_id, display_label: editingOp.display_label || "", vendor_name: editingOp.vendor_name || "",
          vendor_phone: editingOp.vendor_phone || "", vendor_email: editingOp.vendor_email || "", orders_email: editingOp.orders_email || "",
          bank_name: editingOp.bank_name || "", bank_account_number: editingOp.bank_account_number || "", bank_clabe: editingOp.bank_clabe || "",
          account_holder: editingOp.account_holder || "", notes: editingOp.notes || "",
        }
      : { ...EMPTY_FORM, branch_id: presetBranchId || "" }
  );
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const allOps = supplier.branch_operations || [];
  const usedBranchIds = new Set(allOps.filter((o) => o.active !== false && o.id !== editingOp?.id).map((o) => o.branch_id));
  const availableBranches = editingOp
    ? branches
    : branches.filter((b) => !usedBranchIds.has(b.id));
  const duplicateWarning = usedBranchIds.has(form.branch_id);

  const save = async () => {
    if (!form.branch_id) {
      toast.error(t("supplierEdit.branchOps.selectBranchError"));
      return;
    }
    if (form.bank_clabe && !/^\d{18}$/.test(form.bank_clabe)) {
      toast.error(t("supplierEdit.branchOps.clabeError"));
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (editingOp) {
        await api.put(`/suppliers/${supplier.id}/branch-operations/${editingOp.id}`, payload);
      } else {
        await api.post(`/suppliers/${supplier.id}/branch-operations`, payload);
      }
      toast.success(t("supplierEdit.branchOps.saved"));
      onSaved && onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
        onClick={(e) => e.stopPropagation()}
        data-testid="branch-op-modal"
        className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4"
      >
        <div className="flex items-center justify-between">
          <div className="text-base font-bold text-[#1A1A1A]">
            {editingOp ? t("supplierEdit.branchOps.editTitle") : t("supplierEdit.branchOps.newTitle")}
          </div>
          <button data-testid="branch-op-modal-close" onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("supplierEdit.branchOps.branch")}</label>
            <select data-testid="branch-op-field-branch_id" value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)}
              disabled={!!editingOp}
              className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] disabled:opacity-60">
              <option value="">{t("supplierEdit.branchOps.selectBranch")}</option>
              {availableBranches.map((b) => (
                <option key={b.id} value={b.id}>{b.name} ({branchCodeOf(b)})</option>
              ))}
            </select>
            {duplicateWarning && (
              <div data-testid="branch-op-duplicate-warning" className="mt-2 flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>{t("supplierEdit.branchOps.duplicateBranchWarning")}</span>
              </div>
            )}
          </div>
          {[
            ["display_label", "text"], ["vendor_name", "text"], ["vendor_phone", "text"], ["vendor_email", "email"],
            ["orders_email", "email"], ["bank_name", "text"], ["bank_account_number", "text"], ["bank_clabe", "text"], ["account_holder", "text"],
          ].map(([k, type]) => (
            <div key={k}>
              <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t(`supplierEdit.branchOps.fields.${k}`)}</label>
              <input data-testid={`branch-op-field-${k}`} type={type} value={form[k]} onChange={(e) => set(k, e.target.value)}
                className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>
          ))}
          <div className="sm:col-span-2">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("supplierEdit.branchOps.fields.notes")}</label>
            <textarea data-testid="branch-op-field-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
              className="mt-1.5 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            <p className="mt-1 text-[11px] text-slate-400">{t("supplierEdit.branchOps.notesHint")}</p>
          </div>
        </div>

        <div className="flex gap-2 pt-1">
          <button data-testid="branch-op-cancel-btn" onClick={onClose} disabled={busy}
            className="flex-1 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">
            {t("supplierEdit.cancelButton")}
          </button>
          <button data-testid="branch-op-save-btn" onClick={save} disabled={busy}
            className="flex-1 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Save className="w-4 h-4" /> {t("supplierEdit.saveButton")}
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
